import { formatWhen, isScheduled, sendMoment, smsParts } from '../../../../../../../utils/sms';

/**
 * One row of the active, inactive or archived message tables, worked out
 * once when a page of messages arrives so the template only reads fields.
 */
export interface MessageRow {
  /** The API record, handed back to the tables' existing handlers as-is. */
  message: any;
  /** Stable `@for` key: the id, else the uuid, else the row's position. */
  key: string | number;
  subject: string;
  /** The SMS body, falling back to the template text. */
  copy: string;
  from: string;
  sendTime: string;
  created: string;
  /** "1 part", "3 parts", or a dash when there is no copy. */
  parts: string;
  /** Explains the count when personalisation fields make it an estimate. */
  partsHint: string | null;
  /** Lower-cased status, for choosing row actions. */
  status: string;
  /** Sent messages have delivery receipts; the others show a dash. */
  sent: boolean;
}

export function toMessageRows(messages: unknown): MessageRow[] {
  if (!Array.isArray(messages)) return [];

  return messages
    .filter((message) => !!message && typeof message === 'object')
    .map((message: any, index): MessageRow => {
      const status = String(message.status ?? '').toLowerCase();
      const copy = String(message.content || message.messageTemplate || '');
      const count = copy ? smsParts(copy) : null;
      // A draft or cancelled instant message never went out, so its send
      // time would only repeat when it was created.
      const unsent = /^(draft|cancell?ed)$/.test(status) && !isScheduled(message);

      return {
        message,
        key: message.id ?? message.uuid ?? `row-${index}`,
        subject: String(message.messageSubject ?? '').trim() || 'Untitled message',
        copy,
        from: String(message.from ?? '').trim() || '—',
        sendTime: unsent ? '—' : formatWhen(sendMoment(message)),
        created: formatWhen(message.createdAt),
        parts: !count?.parts ? '—' : count.parts === 1 ? '1 part' : `${count.parts} parts`,
        partsHint: count?.estimated
          ? 'Estimate: personalised fields change the length for each contact'
          : null,
        status,
        sent: /^(completed|active)$/.test(status),
      };
    });
}
